/* eslint-disable radix */
import React, { ChangeEvent, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { ArrayHelpers, FormikErrors, FormikProps } from 'formik';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import {
  AlertContent,
  Button,
  Empty,
  ErrorText,
  Input,
  IUserProps,
  LayoutSidebar,
  Message,
  ModalFactory,
  PageTitle,
  SectionHeader,
  SidebarButton,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarHeaderTitle,
  SidebarList,
  SidebarListItem,
  SidebarListItemLabel,
  SidebarListItemValue,
  SidebarTitle,
  TipPoolLimit,
  TooltipIcon,
  TooltipWrapper,
} from 'components';
import { ISelectOption } from 'components/Select/types';
import { appConfig } from 'config/app';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme } from 'design';
import { Appearance } from 'design/types';
import { SelectUser } from 'modals';
import { selectCurrentEmployeeRole, selectCurrentEmployeeType } from 'store/Employers';
import { ApiQueryOperator, EmployeeType, IEmployee, IHoldbackFixed, IHoldbackPercent, IJob, IPayee, ISiteRolePolicy } from 'types';
import { calculateHoldbackValue, capitalize, centsToDollarsDisplay, formatJobTitle, getRolePolicy } from 'utils';
import * as API from '../../api';
import { AnimatedSidebarSection, DescriptionFieldWrapper, MessageContainer, MPContent, PayeesContainer, PaymentsList, PaymentsListItem, SidebarContentLayout, SidebarHeaderSection, SidebarMobileActions, SidebarSection } from './components';
import MakePaymentHeader from './MakePaymentHeader';
import { MakePaymentProps, MakePaymentSidebarProps } from './MakePaymentTypes';

interface IMakePaymentForm {
  payees: IPayee[];
  description: string;
  authorizedAgreement: boolean;
  businessDate: string;
}

interface IPaymentInputProps extends MakePaymentProps, MakePaymentSidebarProps {
  payees: IPayee[];
  isPaymentUpload: boolean;
  isEmployeeDisabled: (emp: IEmployee) => string;
  totalTips: number;
  totalInstantTips: number;
  totalPayrollTips: number;
  tipPoolLimit?: number;
  setTipPoolLimit: (tipPoolLimit: number | undefined) => void;
  onAddForgottenEmployeeSelect: (emp: IEmployee) => void;
  onRemovePayee: (index: number) => void;
  employeeTipLimit: number;
  siteUsesHoldbacks: boolean;
  totalHoldbacks: number;
  siteHoldbacks: IHoldbackFixed | IHoldbackPercent;
  rolePolicy?: ISiteRolePolicy;
  formik: FormikProps<IMakePaymentForm>;
  // these are convenience methods provided by Formik that we must use to manipulate any sort of payee data in a manner that allows the form to be 'aware' of the changes
  arrayHelpers: ArrayHelpers;
  showOverdraftWarning: boolean;
  overdraftText: React.ReactNode;
  overdraftContinue: boolean;
  tipPoolEnabled: boolean;
  strictTipPool: boolean;
  setTipPoolEnabled: (enabled: boolean) => void;
}

const TooltipContent = styled.p`
  color: ${theme.color.neutral[100]};
  white-space: pre-wrap;
`;

const transformEmployee = (employee: IEmployee, disabledReason: string): IUserProps => {
  const { sites } = employee;
  const siteText: string = (() => {
    if (sites.length > 1 || sites.length === 0) {
      if (employee.type === EmployeeType.ADMIN) return 'All Sites';
      return `${sites.length} Sites`;
    }
    if (sites.length === 1) {
      return sites[0].name;
    }
    return '';
  })();
  const roleText: string = (() => {
    if (employee.type === EmployeeType.ADMIN) return formatJobTitle(DEFAULT_ORG_ADMIN_JOB_TITLE);

    const roles = sites.map((site) => site.role);
    const uniqueRoles = new Set(roles);

    if (uniqueRoles.size > 1 || uniqueRoles.size === 0) {
      return `${sites.length} Roles`;
    }
    if (uniqueRoles.size === 1) {
      return capitalize(roles[0]);
    }
    return '';
  })();

  return {
    id: employee.id,
    username: employee.name,
    image: employee.user?.avatarUrl,
    hasPaymentMethod: !!employee.user?.card,
    role: roleText,
    site: siteText,
    disabled: !!disabledReason,
    disabledReason,
  };
};

const PaymentInput = ({
  balance,
  onBack,
  onExpand,
  onNext,
  onRemovePayee,
  sidebarOpen,
  currentSite,
  payees,
  totalTips,
  totalInstantTips,
  totalPayrollTips,
  tipPoolLimit,
  setTipPoolLimit,
  employeeTipLimit,
  totalHoldbacks,
  onAddForgottenEmployeeSelect,
  formik,
  arrayHelpers,
  showOverdraftWarning,
  overdraftText,
  overdraftContinue,
  tipPoolEnabled,
  setTipPoolEnabled,
  isPaymentUpload,
  isEmployeeDisabled,
  siteHoldbacks,
  rolePolicy,
  siteUsesHoldbacks = false,
  strictTipPool = false,
}: IPaymentInputProps) => {
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const [deletePayee, setDeletePayee] = useState<() => void>(() => {});
  const pageSize = 4;
  const [limit, setLimit] = useState<number>(pageSize);
  const [searchName, setSearchName] = useState<string>('');
  const [searchedPayees, setSearchedPayees] = useState<IEmployee[]>();
  const currentEmployeePolicy = getRolePolicy(rolePolicy, currentEmployeeType, currentEmployeeRole);

  // Data
  // Payout Limit
  const calcTipPoolLimit = tipPoolLimit !== undefined && tipPoolLimit > 0 ? tipPoolLimit : 0.0;

  // Remaining Payout
  const remainingTipPool: number | undefined = tipPoolLimit !== undefined ? tipPoolLimit - totalTips : 0 - totalTips;
  const calcRemainingTipPool = remainingTipPool && remainingTipPool <= 0 ? 0 : remainingTipPool;

  const calcTipPoolOver: number | undefined = (() => {
    if (remainingTipPool === undefined) return undefined;

    return remainingTipPool < 0 ? (tipPoolLimit || 0) - totalTips : 0;
  })();

  // Totals
  // change this later when we do holdbacks
  const totalPaid = siteUsesHoldbacks ? totalInstantTips - totalHoldbacks : totalInstantTips;
  const totalNet = siteUsesHoldbacks ? totalTips - totalHoldbacks : totalTips;
  const overTipPoolLimit = Boolean(remainingTipPool && remainingTipPool < 0);

  const fetchEmployees = (searchName?: string, limit = pageSize) => {
    const q = searchName ? [{ key: 'name', operator: ApiQueryOperator.like, value: searchName }] : [];
    API.Sites.listEmployees(currentSite.id, { q, sort: 'name', limit: limit + pageSize + 1 })
      .then((res) => setSearchedPayees(res.body.data))
      .catch(() => setSearchedPayees([]));
  };

  const [showAddPayeeModal, hideAddPayeeModal] = useModal(() => {
    const delayedNameChange = debounce((changedName: string) => {
      setLimit(pageSize);
      setSearchName(changedName);
      fetchEmployees(changedName, pageSize);
    }, 500);

    const loadMore = () => {
      setLimit(limit + pageSize);
      fetchEmployees(searchName, limit + pageSize);
    };

    const onClose = () => {
      setLimit(pageSize);
      setSearchName('');
      setSearchedPayees(undefined);
      hideAddPayeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideAddPayeeModal} isActive>
        <SelectUser
          title="Add User"
          users={searchedPayees?.map((emp) => transformEmployee(emp, isEmployeeDisabled(emp)))}
          isActive
          isLoading={!searchedPayees}
          onSelect={(id) => {
            onAddForgottenEmployeeSelect(searchedPayees?.find((emp) => emp.id === id));
            onClose();
          }}
          onNameChange={delayedNameChange}
          loadMore={loadMore}
          onClose={onClose}
          limit={limit}
        />
      </ModalFactory>
    );
  }, [limit, searchName, searchedPayees, isEmployeeDisabled]);

  const onAddForgottenEmployeeBtnClick = () => {
    fetchEmployees();
    showAddPayeeModal();
  };

  // Remove User Modal
  const [showRemovePayeeModal, hideRemovePayeeModal] = useModal(() => {
    const handleRemovePayee = () => {
      deletePayee();
      hideRemovePayeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideRemovePayeeModal} isActive>
        <AlertContent title="Remove payee?" message="Are you sure you want to remove this payee?" leftActionLabel="Cancel" rightActionLabel="Remove" onLeftAction={hideRemovePayeeModal} onRightAction={handleRemovePayee} />
      </ModalFactory>
    );
  }, [deletePayee]);

  const disableNext = formik.touched.payees?.length !== payees.length || !!formik.errors.payees || formik.isValidating || (strictTipPool && remainingTipPool !== 0) || !overdraftContinue;

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader pageHeaderContent={<PageTitle title={currentSite.name} data-cy="account-balance" subtitle={`Account Balance: ${centsToDollarsDisplay(balance)}`} />} />

          <MPContent>
            <SectionHeader title={`Payees (${payees.length})`}>
              <TipPoolLimit
                isActive={tipPoolEnabled}
                onToggle={() => setTipPoolEnabled(!tipPoolEnabled)}
                value={tipPoolLimit}
                isStrict={strictTipPool}
                onChange={(value) => {
                  setTipPoolLimit(isEmpty(value) ? undefined : parseInt(value));
                }}
                data-cy="tip-pool-limit"
              />
              <Button label="Add User" appearance="white" size="small" data-cy="add-forgotten-employee-btn" onClick={onAddForgottenEmployeeBtnClick} />
            </SectionHeader>

            <PayeesContainer>
              <PaymentsList data-cy="payments-input-list">
                {payees.length === 0 ? (
                  <Empty container containerHeight={250} icon="People" message="No Payees" buttonLabel="Add User" onClick={onAddForgottenEmployeeBtnClick} />
                ) : (
                  payees.map((payee: IPayee, index: number) => (
                    <PaymentsListItem
                      key={`payee_${payee.id}`}
                      hasHoldback={siteUsesHoldbacks}
                      tipAmount={payee.tipAmount}
                      siteId={currentSite.id}
                      siteHoldbacks={siteHoldbacks}
                      holdback={payee.holdbackAmount || payee.holdbackAmount === 0 ? payee.holdbackAmount : 0}
                      note={payee.note}
                      currentEmployeePolicy={currentEmployeePolicy}
                      data-cy="payee-input-list-item"
                      onJobChange={(jobOption: ISelectOption<IJob>) => {
                        arrayHelpers.replace(index, {
                          ...payee,
                          holdbackAmount: calculateHoldbackValue(payee.disbursementMethod, siteHoldbacks, payee.tipAmount),
                          job: jobOption?.value,
                        });
                        setTimeout(formik.validateForm, 1000);
                      }}
                      onHoldbackChange={(holdbackAmount: number) => {
                        arrayHelpers.replace(index, {
                          ...payee,
                          holdbackAmount,
                        });
                      }}
                      onTipAmountChange={(tipAmount: number) => {
                        arrayHelpers.replace(index, {
                          ...payee,
                          holdbackAmount: calculateHoldbackValue(payee.disbursementMethod, siteHoldbacks, tipAmount),
                          tipAmount,
                        });
                      }}
                      onNoteChange={(note: string) => {
                        arrayHelpers.replace(index, {
                          ...payee,
                          note,
                        });
                      }}
                      onRemove={() => {
                        setDeletePayee(() => () => {
                          onRemovePayee(index);
                          // Wait for `arrayHelpers` to update after remove, then check if errors for the removed field still exist.
                          // If so, manually call form validation
                          setTimeout(() => {
                            if (formik.errors.payees?.[index]) formik.validateForm();
                          }, 1000);
                        });
                        showRemovePayeeModal();
                      }}
                      payee={payee}
                      index={index}
                      validateUploadedTips={isPaymentUpload}
                      payeeError={!!formik.touched?.payees?.[index] && !!formik.errors?.payees?.[index]}
                      tipAmountError={(formik.errors?.payees?.[index] as FormikErrors<IPayee>)?.tipAmount || (formik.errors?.payees?.[index] as FormikErrors<IPayee>)?.holdbackAmount}
                      setFieldTouched={formik.setFieldTouched}
                      sitePayeeLimitMin={centsToDollarsDisplay(appConfig.TIP.MIN)}
                      sitePayeeLimitMax={employeeTipLimit && centsToDollarsDisplay(employeeTipLimit)}
                    />
                  ))
                )}
              </PaymentsList>
            </PayeesContainer>
          </MPContent>
        </>
      }
      sidebar={
        <>
          <SidebarHeader>
            <SidebarHeaderSection>
              <SidebarHeaderTitle>
                <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />

                <SidebarTitle title="Payment Summary" />
              </SidebarHeaderTitle>

              <SidebarMobileActions columns={2}>
                <Button label="Back" appearance="white" size="small" disabled={false} onClick={onBack} />
                <Button label="Next" data-cy="next-btn" appearance="primary" size="small" disabled={disableNext} onClick={onNext} />
              </SidebarMobileActions>
            </SidebarHeaderSection>
          </SidebarHeader>

          <SidebarContent>
            <SidebarContentLayout data-cy="payments-input-sidebar">
              <AnimatedSidebarSection
                initial={tipPoolEnabled}
                animate={{
                  height: tipPoolEnabled ? 'auto' : 0,
                }}
                transition={theme.transition.spring.accordion}
                isVisible={tipPoolEnabled}
              >
                <SidebarList>
                  <SidebarListItem>
                    <SidebarListItemLabel size="large">
                      <TooltipWrapper>
                        Payout Limit
                        <TooltipIcon id="limit-description">
                          <TooltipContent>This is the pool of funds you can use to pay employees</TooltipContent>
                        </TooltipIcon>
                      </TooltipWrapper>
                    </SidebarListItemLabel>
                    <SidebarListItemValue data-cy="tip-pool-limit-value">{centsToDollarsDisplay(calcTipPoolLimit)}</SidebarListItemValue>
                  </SidebarListItem>

                  <SidebarListItem>
                    <SidebarListItemLabel size="small">Remaining</SidebarListItemLabel>
                    <SidebarListItemValue data-cy="remaining-tip-pool">{centsToDollarsDisplay(calcRemainingTipPool)}</SidebarListItemValue>
                  </SidebarListItem>

                  {overTipPoolLimit && (
                    <SidebarListItem>
                      <SidebarListItemLabel color="red" size="small">
                        <strong>Over</strong>
                      </SidebarListItemLabel>
                      <SidebarListItemValue color="red" data-cy="over-tip-pool">
                        {calcTipPoolOver ? calcTipPoolOver < 0 && '(' : null}
                        {centsToDollarsDisplay(Math.abs(calcTipPoolOver || 0))}
                        {calcTipPoolOver ? calcTipPoolOver < 0 && ')' : null}
                      </SidebarListItemValue>
                    </SidebarListItem>
                  )}
                  {overTipPoolLimit && tipPoolEnabled && !strictTipPool && (
                    <ErrorText size="small" data-cy="tip-pool-limit-warning">
                      You have exceeded the payout limit. Increase the limit, or change your payouts.
                    </ErrorText>
                  )}
                  {strictTipPool && remainingTipPool !== 0 && payees.length > 0 && (
                    <ErrorText size="small" data-cy="tip-pool-limit-warning">
                      {remainingTipPool > 0 ? 'You have not met the payout limit. Increase your payouts or decrease the limit' : 'You have exceeded the payout limit. Increase the limit, or change your payouts'}
                    </ErrorText>
                  )}
                </SidebarList>
              </AnimatedSidebarSection>

              <SidebarSection>
                <SidebarList>
                  <SidebarListItem>
                    <SidebarListItemLabel size="small">
                      <strong>Amount Entered (Gross)</strong>
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" data-cy="total-paid">
                      <strong>{totalTips > 0 ? centsToDollarsDisplay(totalTips) : '$0.00'}</strong>
                    </SidebarListItemValue>
                  </SidebarListItem>
                  {siteUsesHoldbacks && (
                    <>
                      <SidebarListItem>
                        <SidebarListItemLabel size="small" color="bluegray">
                          <TooltipWrapper>
                            Holdbacks
                            <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
                          </TooltipWrapper>
                        </SidebarListItemLabel>
                        <SidebarListItemValue data-cy="total-holdbacks" size="small" color="bluegray">
                          {`-${centsToDollarsDisplay(totalHoldbacks)}`}
                        </SidebarListItemValue>
                      </SidebarListItem>
                    </>
                  )}
                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Net
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {totalPaid < 0 ? centsToDollarsDisplay(0) : centsToDollarsDisplay(totalNet)}
                    </SidebarListItemValue>
                  </SidebarListItem>

                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Payroll
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {centsToDollarsDisplay(totalPayrollTips)}
                    </SidebarListItemValue>
                  </SidebarListItem>
                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Instant
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {totalPaid < 0 ? centsToDollarsDisplay(0) : centsToDollarsDisplay(totalPaid)}
                    </SidebarListItemValue>
                  </SidebarListItem>

                  <SidebarListItem>
                    <SidebarListItemLabel size="large">Pay Now</SidebarListItemLabel>
                    <SidebarListItemValue size="large" data-cy="total-paid">
                      <strong>{totalPaid > 0 ? centsToDollarsDisplay(totalPaid) : '$0.00'}</strong>
                    </SidebarListItemValue>
                  </SidebarListItem>
                </SidebarList>
              </SidebarSection>

              <SidebarSection>
                <DescriptionFieldWrapper label="Description" optional>
                  <Input
                    placeholder="Description"
                    data-cy="description-input"
                    defaultValue={formik.values.description}
                    onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('description', e.target.value);
                    }}
                    maxLength={100}
                  />
                </DescriptionFieldWrapper>
              </SidebarSection>
              {typeof formik.errors?.payees === 'string' && (
                <MessageContainer>
                  <Message dataCy="payees-error" appearance={Appearance.DANGER} message={formik.errors?.payees} />
                </MessageContainer>
              )}
              {showOverdraftWarning && (
                <MessageContainer>
                  <Message dataCy="overdraft-warning" key="overdraftwarning" appearance={Appearance.WARNING} message={overdraftText} />
                </MessageContainer>
              )}
            </SidebarContentLayout>
          </SidebarContent>

          <SidebarFooter>
            <Button label="Back" appearance="white" size="large" disabled={false} onClick={onBack} data-cy="payment-input-back-btn" />
            <Button data-cy="payment-input-next-btn" label="Next" appearance="primary" size="large" disabled={disableNext} onClick={onNext} />
          </SidebarFooter>
        </>
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default PaymentInput;
